import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

const ClientTicketCreate = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_id: '',
    category_id: '',
    subcategory_id: '',
    issue_type_id: '',
    priority_id: '',
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProducts();
    fetchIssueTypes();
    fetchPriorities();
  }, []);

  useEffect(() => {
    if (formData.product_id) {
      fetchCategories(formData.product_id);
    }
  }, [formData.product_id]);

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    }
  }, [formData.category_id]);

  const fetchUserProducts = async () => {
    try {
      const response = await api.get('/products');
      // Filter to only show products assigned to this client
      const userProductIds = user?.product_ids || [];
      console.log('ClientTicketCreate - Full user object:', user);
      console.log('ClientTicketCreate - User Product IDs:', userProductIds);
      const userProducts = response.data.filter(p => userProductIds.includes(p.id));
      console.log('ClientTicketCreate - Filtered products:', userProducts);
      setProducts(userProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    }
  };

  const fetchCategories = async (productId) => {
    try {
      const response = await api.get(`/categories?product_id=${productId}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await api.get(`/subcategories?category_id=${categoryId}`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchIssueTypes = async () => {
    try {
      const response = await api.get('/issue-types');
      setIssueTypes(response.data);
    } catch (error) {
      console.error('Error fetching issue types:', error);
    }
  };

  const fetchPriorities = async () => {
    try {
      const response = await api.get('/priorities');
      setPriorities(response.data);
      // Set Critical as default priority for client users
      const criticalPriority = response.data.find(p => p.name === 'Critical');
      if (criticalPriority) {
        setFormData(prev => ({ ...prev, priority_id: criticalPriority.id.toString() }));
      }
    } catch (error) {
      console.error('Error fetching priorities:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title || !String(formData.title).trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description || !String(formData.description).trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.product_id) {
      newErrors.product_id = 'Product is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.issue_type_id) {
      newErrors.issue_type_id = 'Issue Type is required';
    }

    if (!formData.priority_id) {
      newErrors.priority_id = 'Priority is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        product_id: parseInt(formData.product_id),
        category_id: parseInt(formData.category_id),
        subcategory_id: formData.subcategory_id ? parseInt(formData.subcategory_id) : null,
        issue_type_id: parseInt(formData.issue_type_id),
        priority_id: parseInt(formData.priority_id),
      };

      const response = await api.post('/tickets', payload);
      toast.success('Ticket created successfully!');
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  if (products.length === 0 && !loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Create New Ticket</h1>
          <p className="text-slate-600 mt-1.5">Submit a new support request</p>
        </div>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
          <CardContent className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
              <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-slate-700 dark:text-white font-medium mb-2">No Products Assigned</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              You don't have access to any products yet. Please contact your administrator to get products assigned to your account.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mt-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-ticket-create-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="gap-2 hover:bg-slate-100"
        >
          <ArrowLeft size={18} />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Create New Ticket</h1>
          <p className="text-slate-600 mt-1.5">Submit a new support request</p>
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
        <CardHeader>
          <CardTitle className="text-xl dark:text-white">Ticket Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="ticket-form">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-semibold text-slate-700">Title *</Label>
              <Input
                id="title"
                data-testid="ticket-title-input"
                placeholder="Brief description of the issue"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: '' });
                }}
                required
                className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                  errors.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
              />
              {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-semibold text-slate-700">Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => {
                  setFormData({ ...formData, description: value });
                  if (errors.description) setErrors({ ...errors, description: '' });
                }}
                placeholder="Detailed description of the issue..."
              />
              {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="product" className="text-sm font-semibold text-slate-700">Product *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, product_id: value, category_id: '', subcategory_id: '' });
                    setCategories([]);
                    setSubcategories([]);
                    if (errors.product_id) setErrors({ ...errors, product_id: '' });
                  }}
                  required
                >
                  <SelectTrigger 
                    data-testid="product-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.product_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.product_id && <p className="text-sm text-red-500">{errors.product_id}</p>}
                <p className="text-xs text-slate-500">Select from your assigned products</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-semibold text-slate-700">Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, category_id: value, subcategory_id: '' });
                    setSubcategories([]);
                    if (errors.category_id) setErrors({ ...errors, category_id: '' });
                  }}
                  disabled={!formData.product_id}
                  required
                >
                  <SelectTrigger 
                    data-testid="category-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && <p className="text-sm text-red-500">{errors.category_id}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory" className="text-sm font-semibold text-slate-700">Subcategory</Label>
                <Select
                  value={formData.subcategory_id}
                  onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                  disabled={!formData.category_id || subcategories.length === 0}
                >
                  <SelectTrigger data-testid="subcategory-select">
                    <SelectValue placeholder="Select subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="issue_type" className="text-sm font-semibold text-slate-700">Issue Type *</Label>
                <Select
                  value={formData.issue_type_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, issue_type_id: value });
                    if (errors.issue_type_id) setErrors({ ...errors, issue_type_id: '' });
                  }}
                  required
                >
                  <SelectTrigger 
                    data-testid="issue-type-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.issue_type_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {issueTypes.map((i) => (
                      <SelectItem key={i.id} value={i.id.toString()}>{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.issue_type_id && <p className="text-sm text-red-500">{errors.issue_type_id}</p>}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                data-testid="submit-ticket-btn"
                className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 shadow-lg shadow-indigo-500/30 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Ticket'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="border-slate-300 hover:bg-slate-100"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientTicketCreate;
